import React, { Component } from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import ProfileIcon from 'components/ProfileIcon';
import { userShape, errorShape } from 'utils/shapes';
import fragments from 'utils/graphql/fragments';
import { GET_GAMES_QUERY } from 'utils/graphql/queries';
import './GamePlayerList.css';

const PLAYERS_PER_PAGE = 5;
const BUTTON_DELAY_MS = 500;

class GamePlayerList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      playerListOffset: 0
    };

    this.scrollPrev = this.scrollPrev.bind(this);
    this.scrollNext = this.scrollNext.bind(this);
    this.buttonTimer = 0;

    this.unsubscribeFromPlayerUpdates = undefined;
  }

  componentWillReceiveProps(nextProps) {
    if (!this.unsubscribeFromPlayerUpdates && !nextProps.gamesData.loading) {
      const gameId = getGameId(nextProps);
      if (gameId) {
        this.unsubscribeFromPlayerUpdates = nextProps.subscribeToPlayerUpdates(
          gameId,
          this.props.onAddPlayers,
          this.props.onRemovePlayers
        );
      }
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeFromPlayerUpdates) {
      this.unsubscribeFromPlayerUpdates();
      this.unsubscribeFromPlayerUpdates = undefined;
    }
  }

  // TODO: If players on visible pages haven't changed, return false
  //shouldComponentUpdate(nextProps, nextState)

  canScrollPrev() {
    return this.state.playerListOffset > 0;
  }

  canScrollNext() {
    const gamePlayers = this.props.playerData.gamePlayers;
    if (!gamePlayers || !gamePlayers.players) {
      return false;
    }
    const lastOffset = gamePlayers.players.length - PLAYERS_PER_PAGE;
    return this.state.playerListOffset < lastOffset;
  }

  scrollPrev() {
    if (Date.now() > this.buttonTimer && this.canScrollPrev()) {
      let offset = Math.max(0, this.state.playerListOffset - PLAYERS_PER_PAGE);
      this.setState({ playerListOffset: offset });
      this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
    }
  }

  scrollNext() {
    if (Date.now() > this.buttonTimer && this.canScrollNext()) {
      let offset = this.state.playerListOffset;
      const players = this.props.playerData.gamePlayers.players;
      const lastOffset = players.length - PLAYERS_PER_PAGE;
      offset = Math.min(lastOffset, offset + PLAYERS_PER_PAGE);
      this.setState({ playerListOffset: offset });
      this.buttonTimer = Date.now() + BUTTON_DELAY_MS;
    }
  }

  /*
  onPositionAdded() {
    // Shift the offset in the list so the same players remain visible
    this.setState((prevState) => {
      const offset = prevState.playerListOffset;
      if (offset === 0) { return {}; }
      return { playerListOffset: offset + 1 };
    });
  }

  onPositionRemoved(removed, total) {
    // Shift the offset in the list so the same players remain visible
    this.setState((prevState) => {
      let offset = prevState.playerListOffset;
      if (!offset || (removed > offset && offset < total - PLAYERS_PER_PAGE)) {
        return {};
      }
      return { playerListOffset: offset - 1 };
    });
  }
*/

  appendListPage(result, page, dir) {
    const players = this.props.playerData.gamePlayers.players;
    const sessions = this.props.playerData.gamePlayers.gameSessionIds;
    if (
      (dir > 0 && !this.canScrollNext()) ||
      (dir < 0 && !this.canScrollPrev()) ||
      !players ||
      !sessions
    ) {
      return;
    }
    let start = this.state.playerListOffset + dir * PLAYERS_PER_PAGE;
    let end = Math.min(start + PLAYERS_PER_PAGE, players.length);
    start = Math.max(start, 0);
    const offset = dir < 0 ? PLAYERS_PER_PAGE - (end - start) : 0;

    for (let index = start; index < end; ++index) {
      const user = players[index];
      const session = sessions[index];
      const posClass = 'player ' + page + (index - start + offset);
      result.push(
        <div key={session} className={posClass} title={user.displayName}>
          <ProfileIcon border="" src={user.avatarUrl} size={30} />
          <div className="profile-name">{user.displayName}</div>
        </div>
      );
    }
  }

  render() {
    if (!this.props.playerData) {
      return null;
    }

    const { loading, error, gamePlayers } = this.props.playerData;

    if (loading || error || !gamePlayers) {
      return <div className="game-player-list" />;
    }

    const prevClass = this.canScrollPrev() ? 'fa-cursor' : 'fa-disabled';
    const nextClass = this.canScrollNext() ? 'fa-cursor' : 'fa-disabled';

    let players = [];
    this.appendListPage(players, 'prev', -1);
    this.appendListPage(players, 'curr', 0);
    this.appendListPage(players, 'next', +1);

    return (
      <div className="game-player-list">
        <div className="player-count">
          <div className="player-count-value">{gamePlayers.totalCount}</div>
          <div className="player-count-label">Playing</div>
        </div>
        <div className="player-list">
          <span onClick={this.scrollPrev} className="scroll-button">
            <i className={`fa fa-chevron-left ${prevClass}`} />
          </span>

          <div className="pages">{players}</div>

          <span onClick={this.scrollNext} className="scroll-button">
            <i className={`fa fa-chevron-right ${nextClass}`} />
          </span>
        </div>
      </div>
    );
  }
}

function getGameId(props) {
  const { loading, error, games } = props.gamesData;
  if (props.gameName && !loading && !error && games && games.games) {
    const game = games.games.find(g => g.name === props.gameName);
    return game && parseInt(game.id, 10);
  }
  return undefined;
}

GamePlayerList.propTypes = {
  gameName: PropTypes.string.isRequired,
  tournamentId: PropTypes.string,
  onAddPlayers: PropTypes.func,
  onRemovePlayers: PropTypes.func,
  gamesData: PropTypes.shape({
    games: PropTypes.shape({
      games: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string
        })
      )
    }),
    loading: PropTypes.bool,
    error: errorShape
  }),
  playerData: PropTypes.shape({
    gamePlayers: PropTypes.shape({
      players: PropTypes.arrayOf(userShape),
      nextId: PropTypes.number,
      totalCount: PropTypes.number
    }),
    loading: PropTypes.bool,
    error: errorShape
  })
};

const QUERY_GET_GAME_PLAYERS = gql`
  query MyGetGamePlayers($input: GetGamePlayers!) {
    gamePlayers(input: $input) {
      gameSessionIds
      players {
        ...UserIdAndDisplayFields
      }
      nextId
      totalCount
    }
  }
  ${fragments.userIdAndDisplay}
`;

const SUBSCRIPTION_GAME_PLAYERS_UPDATES = gql`
  subscription MyGamePlayerUpdates($input: GamePlayerUpdatesInput!) {
    gamePlayerUpdates(input: $input) {
      action
      gameSessionIds
      players {
        ...UserIdAndDisplayFields
      }
    }
  }
  ${fragments.userIdAndDisplay}
`;

export default compose(
  graphql(GET_GAMES_QUERY, {
    name: 'gamesData'
  }),
  graphql(QUERY_GET_GAME_PLAYERS, {
    name: 'playerData',
    skip: props => !getGameId(props),
    options: props => ({
      variables: {
        input: {
          gameId: getGameId(props),
          tournamentId: parseInt(props.tournamentId, 10) || null,
          count: 1000
        }
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps, playerData }) => ({
      playerData,
      subscribeToPlayerUpdates: (gameId, onAdd, onRemove) => {
        return playerData.subscribeToMore({
          document: SUBSCRIPTION_GAME_PLAYERS_UPDATES,
          variables: {
            input: {
              gameId: gameId,
              tournamentId: null
            }
          },
          updateQuery: (prev, { subscriptionData: { data } }) => {
            if (!data || !data.players || !data.players.length) {
              return prev;
            }
            let result = cloneDeep(prev); // NOTE: Do not modify prev

            const players = result.gamePlayers.players;
            const sessions = result.gamePlayers.gameSessionIds;
            const newPlayers = data.players;
            const newSessions = data.gameSessionIds;
            const length = Math.min(newPlayers.length, newSessions.length);

            if (data.action === 'Add') {
              let added = newPlayers.slice(0, length);
              // Remove any older sessions for this player
              for (let index = length - 1; index >= 0; --index) {
                const player = newPlayers[index];
                const match = players.findIndex(
                  p => p.displayName === player.displayName
                );
                if (match >= 0) {
                  sessions.splice(match, 1);
                  players.splice(match, 1);
                  added.splice(index, 1);
                }
              }
              // Add the new sessions to the front
              result.gamePlayers.totalCount += added.length;
              players.unshift(...newPlayers);
              sessions.unshift(...newSessions);
              if (onAdd) {
                onAdd(added);
              }
            } else if (data.action === 'Remove') {
              let removed = [];
              // Remove closed sessions, but don't announce stale sessions
              for (let index = 0; index < length; ++index) {
                const session = newSessions[index];
                const player = newPlayers[index];
                const match = sessions.findIndex(s => s === session);
                if (match >= 0) {
                  sessions.splice(match, 1);
                  players.splice(match, 1);
                  removed.push(player);
                } else if (
                  session <= result.gamePlayers.nextId &&
                  !players.find(p => p.displayName === player.displayName)
                ) {
                  // We don't know anything about this session, assume it's real
                  removed.push(player);
                }
              }
              result.gamePlayers.totalCount = Math.max(
                result.gamePlayers.totalCount - removed.length,
                players.length
              );
              if (onRemove) {
                onRemove(removed);
              }
            }

            return result;
          }
        });
      }
    })
  })
)(GamePlayerList);



// WEBPACK FOOTER //
// ./src/components/GamePlayerList/index.js